import React, { useRef, useState } from "react"
import { Accordion, AccordionSummary, AccordionDetails, Button, Dialog, Link, Typography } from "@mui/material"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import Bowser from "bowser"
import { MediaPermissionsErrorType, requestMediaPermissions } from "mic-check"

const browser = Bowser.getParser(window.navigator.userAgent)

var DialogType;

(function(DialogType) {
  DialogType["explanation"] = "explanation"
  DialogType["systemDenied"] = "systemDenied"
  DialogType["userDenied"] = "userDenied"
  DialogType["trackError"] = "trackError"
})(DialogType || (DialogType = {}))

const MediaOnboardingDialog = () => {
  const [showDialog, setShowDialog] = useState(null)
  const [audioAllowed, setAudioAllowed] = useState(false)
  const [videoAllowed, setVideoAllowed] = useState(false)
  const [errorDetails, setErrorDetails] = useState()

  const showDialogRef = useRef(showDialog)
  showDialogRef.current = showDialog
  const audioAllowedRef = useRef(audioAllowed)
  audioAllowedRef.current = audioAllowed
  const videoAllowedRef = useRef(videoAllowed)
  videoAllowedRef.current = videoAllowed

  React.useEffect(() => {
    checkMediaPermissions()
  }, [])

  React.useEffect(() => {
    if (audioAllowed || videoAllowed) {
      // set the default devices
      // MediaManager.findMediaDevices();
    }
  }, [audioAllowed, videoAllowed])

  const checkForExplanationDialog = () => {
    if ((!audioAllowedRef.current || !videoAllowedRef.current) && showDialogRef.current === null){
      setShowDialog(DialogType.explanation)
    }
  }

  const checkMediaPermissions =  () => {
    // TODO: listen to if there is a change on the audio/video piece?
    requestMediaPermissions()
      .then(() => {
        setAudioAllowed(true)
        setVideoAllowed(true)
        setShowDialog(null)
      })
      .catch(error => {
        if (error.type === MediaPermissionsErrorType.SystemPermissionDenied || error.message === "Permission denied by system") {
          // system denied permission
          setShowDialog(DialogType.systemDenied)
        } else if (
          error.type === MediaPermissionsErrorType.UserPermissionDenied
        ) {
          // user denied permission
          setShowDialog(DialogType.userDenied)
        } else if (
          error.type === MediaPermissionsErrorType.CouldNotStartVideoSource
        ) {
          // most likely when other apps or tabs are using the cam/mic (mostly windows)
          setShowDialog(DialogType.trackError)
        } else {
            // all other errors that cant be fixed by the user 
            setShowDialog(DialogType.systemDenied)
        }
        setErrorDetails(error)
      })

    setTimeout(() => {
      checkForExplanationDialog()
    }, 3000)
  }

  const _renderTryAgain = text => {
    return (
      <div style={{ width: "100%", marginTop: 20 }}>
        <Button
          onClick={() => {
            if (browser.getBrowserName() === "Safari") {
              // If on Safari, rechecking permissions results in glitches so just refresh the page
              window.location.reload()
            } else {
              checkMediaPermissions()
            }
          }}
          color="primary"
          style={{ float: "right" }}
        >
          {text ? text : "Retry"}
        </Button>
      </div>
    )
  }

  const _renderErrorMessage = () => {
    if (!errorDetails) return null
    return (
      <div style={{ marginTop: 10 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="caption" style={{ color: "red" }}>
              Error Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption">
              {errorDetails.name}: {errorDetails.message}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
    )
  }

  const _renderExplanationDialog = () => {
    return (
      <div>
        <Typography variant="h5">
          This App needs to use your camera and microphone
        </Typography>
        <Typography variant="subtitle1">
          Look for the pop up box and select allow if you would like to proceed with We Are Friends.
        </Typography>
      </div>
    )
  }

  const _renderUserDeniedDialog = () => {
    return (
      <div>
        <Typography variant="h5">Camera and microphone are blocked</Typography>
        <Typography>
          App requires access to your camera and microphone.{" "}
          {browser.getBrowserName() !== "Safari" && (
            <Typography style={{ color: "red"}}>
              Click the camera blocked icon{" "}
              <img
                alt="icon"
                src={
                  "https://www.gstatic.com/meet/ic_blocked_camera_dark_f401bc8ec538ede48315b75286c1511b.svg"
                }
                style={{ display: "inline" }}
              />{" "}
              in right side of your browser's address bar and allow to access the camera and microphone for this app.
              After allowing, click the Retry button below or refresh the page.
            </Typography>
          )}
        </Typography>
        {_renderErrorMessage()}
        {_renderTryAgain()}
      </div>
    )
  }

  const _renderSystemDeniedDialog = () => {
    const settingsDataByOS = {
      macOS: {
        name: "System Preferences",
        link:
          "x-apple.systempreferences:com.apple.preference.security?Privacy_Camera"
      }
    }

    return (
      <div>
        <Typography variant="h5">
          Oops! It looks like something is wrong with the camera or microphone on this device
        </Typography>
        <Typography>
            There is a problem with your camera or microphone. To fix this problem, 
            please check your camera and microphone settings and make sure they are working properly.
        </Typography>
        {/* <Typography>
          Your browser might not have access to your camera or microphone. To
          fix this problem, open{" "}
          {// @ts-ignore
          settingsDataByOS[browser.getOSName()] ? (
            <Link
              onClick={() => {
                window.open(
                  // @ts-ignore
                  settingsDataByOS[browser.getOSName()].link,
                  "_blank"
                )
              }}
            >
              {
                // @ts-ignore
                settingsDataByOS[browser.getOSName()].name
              }
            </Link>
          ) : (
            "Settings"
          )}
          .
        </Typography> */}
        {_renderErrorMessage()}
        {_renderTryAgain()}
      </div>
    )
  }

  const _renderTrackErrorDialog = () => {
    return (
      <div>
        <Typography variant="h5">
          Can't start your camera or microphone
        </Typography>
        <Typography>
          Another application (Zoom, Webex) or browser tab (Google Meet,
          Messenger Video) might already be using your webcam. Please turn off
          other cameras before proceeding.
        </Typography>
        {_renderErrorMessage()}
        {_renderTryAgain()}
      </div>
    )
  }

  const _renderDialogContent = () => {
    switch (showDialog) {
      case DialogType.explanation:
        return _renderExplanationDialog()
      case DialogType.systemDenied:
        return _renderSystemDeniedDialog()
      case DialogType.userDenied:
        return _renderUserDeniedDialog()
      case DialogType.trackError:
        return _renderTrackErrorDialog()
    }
  }
  return (
    <Dialog open={!!showDialog}>{showDialog && _renderDialogContent()}</Dialog>
  )
}

export default MediaOnboardingDialog
